import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { User, DiscordRole, AuthResponse } from '../types/auth';

interface AuthContextType {
  user: User | null;
  discordRoles: DiscordRole[];
  isAuthenticated: boolean;
  isLoading: boolean;
  permissions: string[];
  login: () => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE = '/api';

function normalizeDiscordRoles(rawRoles: unknown): DiscordRole[] {
  if (!Array.isArray(rawRoles)) return [];

  return rawRoles.flatMap((role) => {
    if (typeof role === 'string') {
      return [{
        id: role,
        discordRoleId: role,
        name: role,
        color: null,
        isActive: true,
        permissionCount: 0,
        userCount: 0,
      }];
    }

    if (role && typeof role === 'object') {
      const item = role as Partial<DiscordRole>;
      const id = typeof item.id === 'string' ? item.id : typeof item.discordRoleId === 'string' ? item.discordRoleId : '';
      if (!id) return [];
      return [{
        id,
        discordRoleId: typeof item.discordRoleId === 'string' ? item.discordRoleId : id,
        name: typeof item.name === 'string' ? item.name : id,
        color: typeof item.color === 'string' ? item.color : null,
        isActive: typeof item.isActive === 'boolean' ? item.isActive : false,
        permissionCount: typeof item.permissionCount === 'number' ? item.permissionCount : 0,
        userCount: typeof item.userCount === 'number' ? item.userCount : 0,
      }];
    }

    return [];
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [discordRoles, setDiscordRoles] = useState<DiscordRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [permissions, setPermissions] = useState<string[]>([]);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/me`, {
        credentials: 'include',
        headers: { 'Cache-Control': 'no-cache' },
      });

      if (response.status === 401) {
        setUser(null);
        setDiscordRoles([]);
        setPermissions([]);
        return;
      }

      if (!response.ok) {
        throw new Error('Authentifizierungsstatus konnte nicht geladen werden');
      }

      const data: AuthResponse = await response.json();
      if (data.authenticated && data.user) {
        const authenticatedUser: User = {
          ...data.user,
          discordDisplayName: data.user.displayName,
          discordAvatar: data.user.avatar && data.user.discordUserId
            ? `https://cdn.discordapp.com/avatars/${encodeURIComponent(data.user.discordUserId)}/${encodeURIComponent(data.user.avatar)}.png`
            : data.user.avatar,
          email: null,
          firstName: null,
          lastName: null,
          personnelNumber: null,
          departmentId: null,
          departmentName: null,
          positionId: null,
          position: null,
          isActive: true,
          lastLoginAt: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setUser(authenticatedUser);
        setDiscordRoles(normalizeDiscordRoles(data.discordRoles));
        setPermissions(data.permissions || []);
      } else {
        setUser(null);
        setDiscordRoles([]);
        setPermissions([]);
      }
    } catch {
      setUser(null);
      setDiscordRoles([]);
      setPermissions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(() => {
    window.location.href = `${API_BASE}/auth/discord`;
  }, []);

  const logout = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Cache-Control': 'no-store' },
      });
      if (!response.ok && response.status !== 401) {
        throw new Error('Abmeldung konnte nicht abgeschlossen werden');
      }
    } catch {
    } finally {
      setUser(null);
      setDiscordRoles([]);
      setPermissions([]);
      window.location.href = '/';
    }
  }, []);

  const hasPermission = useCallback((permission: string): boolean => {
    return permissions.includes(permission);
  }, [permissions]);

  return (
    <AuthContext.Provider
      value={{
        user,
        discordRoles,
        isAuthenticated: !!user,
        isLoading,
        permissions,
        login,
        logout,
        checkAuth,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
